import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Divider, Select, MenuItem, Paper, Stack, Avatar } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VideocamIcon from '@mui/icons-material/VideocamOutlined';
import AddIcon from '@mui/icons-material/Add';
import TagIcon from '@mui/icons-material/Tag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const CalendarView = () => {
    const [scheduledMeetings, setScheduledMeetings] = useState([]);

    useEffect(() => {
        const saved = localStorage.getItem('teams_scheduled_meetings');
        if (saved) {
            try {
                setScheduledMeetings(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse scheduled meetings", e);
            }
        }
    }, []);

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
            {/* Top Header */}
            <Box sx={{
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                borderBottom: '1px solid #e1e1e1'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon sx={{ color: '#4b53bc' }} />
                    <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
                        Calendar
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TagIcon />}
                        sx={{ textTransform: 'none', color: '#242424', borderColor: '#d1d1d1' }}
                    >
                        Join with an ID
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VideocamIcon />}
                        sx={{ textTransform: 'none', color: '#242424', borderColor: '#d1d1d1' }}
                    >
                        Meet now
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ textTransform: 'none' }}
                    >
                        New meeting
                    </Button>
                </Box>
            </Box>

            {/* Sub Header / Navigation */}
            <Box sx={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                borderBottom: '1px solid #e1e1e1'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'none', color: '#242424', borderColor: '#d1d1d1', px: 2 }}
                    >
                        Today
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small"><ChevronLeftIcon /></IconButton>
                        <IconButton size="small"><ChevronRightIcon /></IconButton>
                        <Typography sx={{ ml: 1, fontWeight: 600, fontSize: '14px' }}>
                            March {new Date().getFullYear()}
                        </Typography>
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                            <ChevronRightIcon sx={{ transform: 'rotate(90deg)', fontSize: 16 }} />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon fontSize="small" sx={{ color: '#616161' }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>Work week</Typography>
                    <IconButton size="small">
                        <ChevronRightIcon sx={{ transform: 'rotate(90deg)', fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1, p: 3, overflowY: 'auto', bgcolor: '#f5f5f5' }}>
                {scheduledMeetings.length === 0 ? (
                    <Box sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                            No meetings scheduled for this week.
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Click "New meeting" to get started.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {scheduledMeetings.map((meeting) => (
                            <Paper
                                key={meeting.id}
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    borderLeft: '4px solid #4b53bc',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{meeting.title}</Typography>
                                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#616161' }}>
                                                <AccessTimeIcon sx={{ fontSize: 14 }} />
                                                <Typography variant="caption">{meeting.startDate} • {meeting.startTime} - {meeting.endTime}</Typography>
                                            </Box>
                                            {meeting.location && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#616161' }}>
                                                    <LocationOnIcon sx={{ fontSize: 14 }} />
                                                    <Typography variant="caption">{meeting.location}</Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>
                                    <Button size="small" variant="outlined" sx={{ textTransform: 'none' }}>
                                        Join
                                    </Button>
                                </Box>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default CalendarView;
